/**
 * システム管理ページ - superadmin 専用
 * /admin
 *
 * - 全組織管理（一覧・作成・編集・削除）
 * - 全ユーザー管理（一覧・編集・削除）
 * - 全ドローン管理（一覧・編集・削除）
 * - システム統計
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { organizationApi, adminUserApi, adminDroneApi, statsApi } from '../services/adminApi';
import { Icon } from '../components/icons';
import { Button, Card, Badge, Alert, Input, Select, Modal } from '../components/ui';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableEmpty } from '../components/ui';

const ROLE_LABELS = {
  superadmin: 'システム管理者',
  admin: '管理者',
  operator: 'オペレーター',
  viewer: '閲覧者',
};

const ROLE_OPTIONS = ['superadmin', 'admin', 'operator', 'viewer'];

const ROLE_BADGE_VARIANT = {
  superadmin: 'warning',
  admin: 'primary',
  operator: 'success',
  viewer: 'default',
};

const STATUS_BADGE_VARIANT = {
  active: 'success',
  suspended: 'danger',
  deleted: 'default',
  maintenance: 'warning',
  retired: 'default',
};

const TAB_ITEMS = [
  { id: 'organizations', label: '組織管理', icon: 'building' },
  { id: 'users', label: 'ユーザー管理', icon: 'users' },
  { id: 'drones', label: '機体一覧', icon: 'drone' },
];

export default function Admin() {
  const [activeTab, setActiveTab] = useState('organizations');
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // 組織データ
  const [organizations, setOrganizations] = useState([]);
  const [orgLoading, setOrgLoading] = useState(false);

  // ユーザーデータ
  const [users, setUsers] = useState([]);
  const [userLoading, setUserLoading] = useState(false);
  const [filterOrgId, setFilterOrgId] = useState('');

  // ドローンデータ
  const [drones, setDrones] = useState([]);
  const [droneLoading, setDroneLoading] = useState(false);
  const [filterDroneOrgId, setFilterDroneOrgId] = useState('');

  // モーダル状態
  const [showCreateOrgModal, setShowCreateOrgModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  // 新規組織フォーム
  const [newOrgName, setNewOrgName] = useState('');
  const [newOrgType, setNewOrgType] = useState('organization');
  const [creating, setCreating] = useState(false);

  // 統計取得
  useEffect(() => {
    const fetchStats = async () => {
      try {
        const data = await statsApi.get();
        setStats(data);
      } catch (err) {
        console.error('Stats fetch error:', err);
      }
    };
    fetchStats();
  }, []);

  // 組織一覧取得
  const fetchOrganizations = useCallback(async () => {
    setOrgLoading(true);
    setError('');
    try {
      const data = await organizationApi.list({ limit: 100 });
      setOrganizations(data.items || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setOrgLoading(false);
      setLoading(false);
    }
  }, []);

  // ユーザー一覧取得
  const fetchUsers = useCallback(async () => {
    setUserLoading(true);
    try {
      const params = filterOrgId ? { orgId: filterOrgId } : {};
      const data = await adminUserApi.list({ ...params, limit: 100 });
      setUsers(data.items || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setUserLoading(false);
    }
  }, [filterOrgId]);

  // ドローン一覧取得
  const fetchDrones = useCallback(async () => {
    setDroneLoading(true);
    try {
      const params = filterDroneOrgId ? { orgId: filterDroneOrgId } : {};
      const data = await adminDroneApi.list({ ...params, limit: 100 });
      setDrones(data.items || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setDroneLoading(false);
    }
  }, [filterDroneOrgId]);

  // 初期データ取得
  useEffect(() => {
    fetchOrganizations();
  }, [fetchOrganizations]);

  // タブ切り替え時のデータ取得
  useEffect(() => {
    if (activeTab === 'users' && users.length === 0) {
      fetchUsers();
    } else if (activeTab === 'drones' && drones.length === 0) {
      fetchDrones();
    }
  }, [activeTab, fetchUsers, fetchDrones, users.length, drones.length]);

  // フィルタ変更時の再取得
  useEffect(() => {
    if (activeTab === 'users') fetchUsers();
  }, [filterOrgId, activeTab, fetchUsers]);

  useEffect(() => {
    if (activeTab === 'drones') fetchDrones();
  }, [filterDroneOrgId, activeTab, fetchDrones]);

  // 組織作成
  const handleCreateOrg = async (e) => {
    e.preventDefault();
    if (!newOrgName.trim()) return;

    setCreating(true);
    try {
      await organizationApi.create({ orgName: newOrgName, orgType: newOrgType });
      setShowCreateOrgModal(false);
      setNewOrgName('');
      fetchOrganizations();
    } catch (err) {
      alert('組織作成に失敗しました: ' + err.message);
    } finally {
      setCreating(false);
    }
  };

  // 組織削除
  const handleDeleteOrg = async (org) => {
    if (!window.confirm(`「${org.orgName}」を削除しますか？\nこの操作は取り消せません。`)) return;

    try {
      await organizationApi.delete(org.orgId);
      fetchOrganizations();
    } catch (err) {
      alert('組織削除に失敗しました: ' + err.message);
    }
  };

  // ユーザー編集モーダルを開く
  const openEditUserModal = (user) => {
    setEditingUser({ ...user, originalOrgId: user.orgId });
    setShowEditUserModal(true);
  };

  // ユーザー更新
  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;

    try {
      const updateData = {
        currentOrgId: editingUser.originalOrgId,
        role: editingUser.role,
        status: editingUser.status,
        displayName: editingUser.displayName,
      };
      // 組織が変更された場合のみ新しいorgIdを送信
      if (editingUser.orgId !== editingUser.originalOrgId) {
        updateData.orgId = editingUser.orgId;
      }
      await adminUserApi.update(editingUser.userId, updateData);
      setShowEditUserModal(false);
      setEditingUser(null);
      fetchUsers();
    } catch (err) {
      alert('ユーザー更新に失敗しました: ' + err.message);
    }
  };

  // ユーザー削除
  const handleDeleteUser = async (user) => {
    if (!window.confirm(`「${user.displayName || user.email}」を削除しますか？\nこの操作は取り消せません。`)) return;

    try {
      await adminUserApi.delete(user.userId, user.orgId);
      fetchUsers();
    } catch (err) {
      alert('ユーザー削除に失敗しました: ' + err.message);
    }
  };


  // 組織名を取得するヘルパー
  const getOrgName = (orgId) => {
    const org = organizations.find(o => o.orgId === orgId);
    return org?.orgName || orgId;
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-slate-900">システム管理</h1>
          <Badge variant="warning">superadmin</Badge>
        </div>
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-slate-500">読み込み中...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-slate-900">システム管理</h1>
          <Badge variant="warning">superadmin</Badge>
        </div>
      </div>

      {/* 統計カード */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                <Icon name="building" className="w-6 h-6 text-primary-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.organizations}</p>
                <p className="text-sm text-slate-500">組織</p>
              </div>
            </div>
          </Card>
          <Card>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                <Icon name="users" className="w-6 h-6 text-emerald-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.users}</p>
                <p className="text-sm text-slate-500">ユーザー</p>
              </div>
            </div>
          </Card>
          <Card>
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                <Icon name="drone" className="w-6 h-6 text-amber-500" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{stats.drones}</p>
                <p className="text-sm text-slate-500">ドローン</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {error && <Alert variant="error" onClose={() => setError('')}>{error}</Alert>}

      {/* タブ */}
      <div className="border-b border-slate-200">
        <nav className="flex gap-4">
          {TAB_ITEMS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors
                ${activeTab === tab.id
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }
              `}
            >
              <Icon name={tab.icon} className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* 組織管理タブ */}
      {activeTab === 'organizations' && (
        <Card
          title={`組織一覧 (${organizations.length}件)`}
          headerActions={
            <Button size="sm" onClick={() => setShowCreateOrgModal(true)}>
              <Icon name="plus" className="w-4 h-4" />
              新規作成
            </Button>
          }
        >
          {orgLoading ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>組織名</TableHead>
                  <TableHead>タイプ</TableHead>
                  <TableHead>ユーザー数</TableHead>
                  <TableHead>プラン</TableHead>
                  <TableHead>ステータス</TableHead>
                  <TableHead>作成日</TableHead>
                  <TableHead>操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {organizations.length === 0 ? (
                  <TableEmpty
                    message="組織がありません"
                    icon={<Icon name="building" className="w-8 h-8" />}
                  />
                ) : (
                  organizations.map((org) => (
                    <TableRow key={org.orgId}>
                      <TableCell className="font-medium text-slate-900">{org.orgName}</TableCell>
                      <TableCell>
                        <Badge variant={org.orgType === 'organization' ? 'primary' : 'default'}>
                          {org.orgType === 'organization' ? '組織' : '個人'}
                        </Badge>
                      </TableCell>
                      <TableCell>{org.userCount ?? '-'}</TableCell>
                      <TableCell>{org.plan || 'free'}</TableCell>
                      <TableCell>
                        <Badge variant={STATUS_BADGE_VARIANT[org.status] || 'default'}>
                          {org.status || 'active'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-500 text-sm">
                        {org.createdAt ? new Date(org.createdAt).toLocaleDateString('ja-JP') : '-'}
                      </TableCell>
                      <TableCell>
                        <Button
                          size="sm"
                          variant="danger-outline"
                          onClick={() => handleDeleteOrg(org)}
                        >
                          <Icon name="trash" className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </Card>
      )}

      {/* ユーザー管理タブ */}
      {activeTab === 'users' && (
        <Card title={`ユーザー一覧 (${users.length}件)`}>
          <div className="mb-4">
            <Select
              label="組織でフィルタ"
              value={filterOrgId}
              onChange={(e) => setFilterOrgId(e.target.value)}
              options={[
                { value: '', label: 'すべての組織' },
                ...organizations.map(o => ({ value: o.orgId, label: o.orgName })),
              ]}
            />
          </div>
          {userLoading ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>名前</TableHead>
                  <TableHead>メール</TableHead>
                  <TableHead>組織</TableHead>
                  <TableHead>ロール</TableHead>
                  <TableHead>ステータス</TableHead>
                  <TableHead>操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.length === 0 ? (
                  <TableEmpty
                    message="ユーザーがいません"
                    icon={<Icon name="users" className="w-8 h-8" />}
                  />
                ) : (
                  users.map((user) => (
                    <TableRow key={user.userId}>
                      <TableCell className="font-medium text-slate-900">
                        {user.displayName || user.email?.split('@')[0]}
                      </TableCell>
                      <TableCell className="text-slate-500">{user.email}</TableCell>
                      <TableCell className="text-sm">{getOrgName(user.orgId)}</TableCell>
                      <TableCell>
                        <Badge variant={ROLE_BADGE_VARIANT[user.role] || 'default'}>
                          {ROLE_LABELS[user.role] || user.role}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={STATUS_BADGE_VARIANT[user.status] || 'success'}>
                          {user.status === 'suspended' ? '停止中' : '有効'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="secondary"
                            onClick={() => openEditUserModal(user)}
                          >
                            <Icon name="edit" className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="danger-outline"
                            onClick={() => handleDeleteUser(user)}
                          >
                            <Icon name="trash" className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </Card>
      )}

      {/* ドローン管理タブ */}
      {activeTab === 'drones' && (
        <Card title={`機体一覧 (${drones.length}件)`}>
          <p className="text-xs text-slate-500 mb-3">
            IoT Core に登録された Thing の一覧（読み取り専用）。編集・削除は各機体のデバイス画面で行います。組織と登録者は登録時に自動記録されます（それ以前の機体は「-」）。
          </p>
          <div className="mb-4">
            <Select
              label="組織でフィルタ（Thing 属性 orgId）"
              value={filterDroneOrgId}
              onChange={(e) => setFilterDroneOrgId(e.target.value)}
              options={[
                { value: '', label: 'すべて' },
                ...organizations.map(o => ({ value: o.orgId, label: o.orgName })),
              ]}
            />
          </div>
          {droneLoading ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>機体名（Thing）</TableHead>
                  <TableHead>タイプ</TableHead>
                  <TableHead>組織</TableHead>
                  <TableHead>登録者</TableHead>
                  <TableHead>担当者</TableHead>
                  <TableHead>ライフサイクル</TableHead>
                  <TableHead>接続</TableHead>
                  <TableHead>最終接続</TableHead>
                  <TableHead>FW</TableHead>
                  <TableHead>操作</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {drones.length === 0 ? (
                  <TableEmpty
                    message="機体がありません"
                    icon={<Icon name="drone" className="w-8 h-8" />}
                  />
                ) : (
                  drones.map((drone) => (
                    <TableRow key={drone.thingName}>
                      <TableCell className="font-medium text-slate-900 font-mono text-sm">{drone.thingName}</TableCell>
                      <TableCell className="text-slate-500">{drone.thingTypeName || '-'}</TableCell>
                      <TableCell className="text-sm">{drone.orgId ? getOrgName(drone.orgId) : '-'}</TableCell>
                      <TableCell className="text-sm text-slate-600">{drone.registeredBy || '-'}</TableCell>
                      <TableCell className="text-sm text-slate-600" title={drone.assigneeEmail || undefined}>
                        {drone.assigneeDisplayName || drone.assigneeEmail || '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={STATUS_BADGE_VARIANT[drone.status] || 'default'}>
                          {drone.status || 'unknown'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {drone.connected === null || drone.connected === undefined ? (
                          <span className="text-slate-400 text-sm">-</span>
                        ) : drone.connected ? (
                          <Badge variant="success" size="sm">接続中</Badge>
                        ) : (
                          <Badge variant="default" size="sm">切断</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-slate-500 text-sm">
                        {drone.lastConnectedAt ? new Date(drone.lastConnectedAt).toLocaleString('ja-JP') : '-'}
                      </TableCell>
                      <TableCell className="text-slate-500 text-sm">{drone.firmwareVersion || '-'}</TableCell>
                      <TableCell>
                        <Link to={`/devices/${encodeURIComponent(drone.thingName)}`} className="text-primary-500 hover:underline text-sm flex items-center gap-1">
                          詳細
                          <Icon name="arrowRight" className="w-4 h-4" />
                        </Link>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </Card>
      )}

      {/* 組織作成モーダル */}
      <Modal
        isOpen={showCreateOrgModal}
        title="新規組織作成"
        onClose={() => setShowCreateOrgModal(false)}
      >
        <form onSubmit={handleCreateOrg} className="space-y-4">
          <Input
            label="組織名"
            value={newOrgName}
            onChange={(e) => setNewOrgName(e.target.value)}
            placeholder="例: 株式会社ドローンテック"
            required
          />
          <Select
            label="組織タイプ"
            value={newOrgType}
            onChange={(e) => setNewOrgType(e.target.value)}
            options={[
              { value: 'organization', label: '組織' },
              { value: 'personal', label: '個人' },
            ]}
          />
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="secondary" onClick={() => setShowCreateOrgModal(false)}>
              キャンセル
            </Button>
            <Button type="submit" loading={creating}>
              作成
            </Button>
          </div>
        </form>
      </Modal>

      {/* ユーザー編集モーダル */}
      <Modal
        isOpen={showEditUserModal}
        title="ユーザー編集"
        onClose={() => {
          setShowEditUserModal(false);
          setEditingUser(null);
        }}
      >
        {editingUser && (
          <form onSubmit={handleUpdateUser} className="space-y-4">
            <Input
              label="表示名"
              value={editingUser.displayName || ''}
              onChange={(e) => setEditingUser({ ...editingUser, displayName: e.target.value })}
            />
            <Input
              label="メールアドレス"
              value={editingUser.email || ''}
              disabled
            />
            <Select
              label="ロール"
              value={editingUser.role}
              onChange={(e) => setEditingUser({ ...editingUser, role: e.target.value })}
              options={ROLE_OPTIONS.map(r => ({ value: r, label: ROLE_LABELS[r] }))}
            />
            <Select
              label="ステータス"
              value={editingUser.status || 'active'}
              onChange={(e) => setEditingUser({ ...editingUser, status: e.target.value })}
              options={[
                { value: 'active', label: '有効' },
                { value: 'suspended', label: '停止' },
              ]}
            />
            <Select
              label="所属組織"
              value={editingUser.orgId || ''}
              onChange={(e) => setEditingUser({ ...editingUser, orgId: e.target.value })}
              options={organizations.map(o => ({ value: o.orgId, label: o.orgName }))}
            />
            {editingUser.orgId !== editingUser.originalOrgId && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                ⚠️ 組織を変更すると、ユーザーは新しい組織のデータにのみアクセスできるようになります。
              </div>
            )}
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setShowEditUserModal(false);
                  setEditingUser(null);
                }}
              >
                キャンセル
              </Button>
              <Button type="submit">
                保存
              </Button>
            </div>
          </form>
        )}
      </Modal>

    </div>
  );
}
